/* UTILS */
import { decodeEntity } from 'html-entities';

import { t } from '@/config/i18n';
import { EMAIL_REGEX } from '@/enums/constants';

type Iteratee<T> = ((item: T) => PropertyKey) | keyof T;
/**
 *  Capitalize first letter of a specified word
 *
 * @param {String} word - word to capitalize
 *
 * @returns  {String} Word with first letter capitalized
 */
export function capitalizeFirstLetter(word) {
  return `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
}
/**
 *  Escape html characters for sanitation
 *
 * @param {String} str - string to sanitize
 * @returns  {String} sanitized string
 */
export function escapeLightHtml(str) {
  return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#x27;');
}

/* Allows to get the timestamp in seconds for the current date */
export function getCurrentTimestamp() {
  const dateNow = new Date();
  return Math.floor(dateNow.getTime() / 1000);
}

/**
 * Get time difference with generic translation key
 *
 * You can have
 *  - * days ago
 *  - * hours ago
 *  - * minutes ago
 *
 * @param {number} timestamp Time in miliseconds
 */
export function getTimeDifferenceText(timestamp: number) {
  // Init now and time for created at notif
  const now = new Date().getTime();
  // notification.created_at is in second from back api
  const notificationTime = new Date(timestamp * 1000).getTime();

  // Compute time difference in miliseconds
  const timeDifferenceMs = now - notificationTime;

  // Get time difference in days, hours and minutes
  const timeDifferenceDays = Math.round(timeDifferenceMs / (1000 * 3600 * 24));
  const timeDifferenceHours = Math.round(timeDifferenceMs / (1000 * 3600));
  const timeDifferenceMinutes = Math.round(timeDifferenceMs / (1000 * 60));

  // Get time type to display in the notification
  let timeType: 'day' | 'hour' | 'minute';
  if (timeDifferenceDays > 0) {
    timeType = 'day';
  } else if (timeDifferenceHours > 0) {
    timeType = 'hour';
  } else {
    timeType = 'minute';
  }

  return t('general.count.time_passed', {
    time: timeType,
    day: timeDifferenceDays,
    hour: timeDifferenceHours,
    minute: timeDifferenceMinutes
  });
}

/**
 * Replaces html entities codes to decode a given string
 * @param {string} str string to decode
 * @returns {string} decoded string
 */
export function decodeHtmlEntity(str) {
  return str.replace(/&#?[\w\d]+;/g, function (match) {
    return decodeEntity(match);
  });
}

/**
 * Allows to compare two object and return one final object with only updated properties
 */
export function getUpdatedFields(object, initialObject) {
  return Object.entries(object).reduce((acc, [key, value]) => {
    if (!isEqual(value, initialObject?.[key])) {
      acc[key] = value;
    }
    return acc;
  }, {});
}

export function downloadFile(input, filename, asPdf = false) {
  const params = asPdf ? { type: 'application/pdf' } : {};
  const blob = new Blob([input], { ...params });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/* Returns a cloned copy of any data */
export function cloneDeep<T>(data: T): T {
  // Case primitive types
  if (data === null || typeof data !== 'object') return data;

  // Case date
  if (data instanceof Date) {
    return new Date(data.getTime()) as T;
  }

  // Case array - clone of items within the array
  if (Array.isArray(data)) {
    return data.map(item => cloneDeep(item)) as T;
  }

  // Case object
  const clonedObject = {} as T;

  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      clonedObject[key] = cloneDeep(data[key]);
    }
  }

  return clonedObject;
}

/* Checks if a given data is empty */
export function isEmpty(data: Record<any, any> | string | Array<any>): boolean {
  if (!data) return true;
  if (typeof data === 'string' || Array.isArray(data)) return data.length === 0;
  if (typeof data === 'object') return Object.keys(data).length === 0;
  return false;
}

/* Split a given array in two arrays according to return of given predicate */
export function partition<T>(array: T[], predicate: (item: T) => boolean): [T[], T[]] {
  const truthy: T[] = [];
  const falsy: T[] = [];

  for (const item of array) {
    if (predicate(item)) {
      truthy.push(item);
    } else {
      falsy.push(item);
    }
  }

  return [truthy, falsy];
}

/* Checks if two data are equal */
export function isEqual(a: any, b: any): boolean {
  if (a === b) return true;

  if (a == null || b == null) return a === b;

  // different types return false
  if (typeof a !== typeof b) return false;

  // Date
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Array
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, i) => isEqual(item, b[i]));
  }

  // Object
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    return keysA.every(key => isEqual(a[key], b[key]));
  }

  return false;
}

/* returns items from the first parameter array that are not included in the second parameter array based on a specific property key or function */
export function differenceBy<T>(array: T[], values: T[], iteratee: Iteratee<T>): T[] {
  const getValue = typeof iteratee === 'function' ? iteratee : (item: T) => item[iteratee] as PropertyKey;

  const excluded = new Set(values.map(getValue));
  return array.filter(item => !excluded.has(getValue(item)));
}

/* Returns items in common between two given arrays */
export function intersection<T>(array: T[], values: T[]): T[] {
  const included = new Set(values);
  const seen = new Set<T>();

  return array.filter(item => {
    if (!included.has(item) || seen.has(item)) return false;

    seen.add(item);
    return true;
  });
}

/* returns from a given array a new array without duplicates. If iteratee is given, deduplication will be done based on a specific function or property key  */
export function uniq<T>(array: T[], iteratee?: Iteratee<T>): T[] {
  if (!iteratee) return [...new Set(array)];

  const getValue = typeof iteratee === 'function' ? iteratee : (item: T) => item[iteratee] as PropertyKey;
  const seen = new Set<PropertyKey>();

  return array.filter(item => {
    const value = getValue(item);

    if (seen.has(value)) return false;

    seen.add(value);
    return true;
  });
}

/* Returns a uniq ID prefixed with a given string */
export function generateUniqueId(prefix: string) {
  return `${prefix}-${crypto.randomUUID()}`;
}

export function testMailValidity(email) {
  return !!email.match(EMAIL_REGEX);
}
